import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { BehaviorSubject, map, mergeMap, Observable, share, tap } from "rxjs";

import { DeliveryApiService } from "../../../shared-library/delivery-api/delivery-api.service";
import { ContentSearchPageModel } from "../../../shared-library/delivery-api/models/content-search-page.model";
import { PaginationOptionsModel } from "../../../shared-library/form-controls/pagination/pagination-options.model";
import { BrowserWindowService } from "../../../shared-library/services/environment/browser-window.service";
import { isRteMarkupEmpty } from "../../../shared-library/utils/rte.utils";
import { BaseSearchResultsModel } from "../models/base-search-results.model";
import { LockService } from "../services/lock.service";
import { PaginationOptionsFactoryService } from "../services/pagination-options-factory.service";

import { ContentSearchConfigurationModel } from "./models/content-search-configuration.model";
import { ContentItemModel } from "./models/content-search.model";
import { SearchParametersModel } from "./models/search-parameters.model";
import { SearchTabConfigModel } from "./models/search-tab-config.model";
import { ContentSearchParametersFactoryService } from "./services/content-search-parameters-factory.service";
import { ContentSearchService } from "./services/content-search.service";

@Component({
    selector: "pcg-ng-content-search",
    templateUrl: "./content-search.component.html"
})
export class ContentSearchComponent implements OnInit, OnDestroy {
    public static SELECTOR: string = "pcg-ng-content-search";

    public paginationOptions: PaginationOptionsModel;

    public configuration: ContentSearchConfigurationModel;
    public searchParameters$: BehaviorSubject<SearchParametersModel>;
    public searchResult$: Observable<BaseSearchResultsModel<ContentItemModel>>;
    @Input() public activeSearchTab: string;
    @Input() public searchTabConfig: SearchTabConfigModel;
    protected pageModel$: Observable<ContentSearchPageModel>;
    protected selectedPageNumber: number;
    protected selectedSorting: string = "0";
    protected selectedSearchText: string = "";
    protected readonly defaultNoResultsMessage: string = "Sorry, no results were found. Please try changing your search criteria.";
    @Output() protected searchTabChange = new EventEmitter<string>();

    public constructor(
        private _browserWindowService: BrowserWindowService,
        private _paginationOptionsFactory: PaginationOptionsFactoryService,
        private _searchParametersFactory: ContentSearchParametersFactoryService,
        private _searchService: ContentSearchService,
        private _deliveryApiService: DeliveryApiService,
        private _lockService: LockService
    ) {
    }

    public get isLocked$(): Observable<boolean> {
        return this._lockService.isLocked$;
    }

    public ngOnInit(): void {
        this.pageModel$ = this._deliveryApiService.getPage$<ContentSearchPageModel>(window.location.pathname);

        this.paginationOptions = this._paginationOptionsFactory.createDefaultPaginationOptions();
        this.paginationOptions.showGoToPrevious = true;
        this.paginationOptions.showGoToNext = true;

        const initialParameters = this._searchParametersFactory.createFromQueryString(this._browserWindowService.getQueryString());
        this.selectedSorting = initialParameters.sortOption;
        this.selectedSearchText = initialParameters.searchText;
        initialParameters.searchTab = this.activeSearchTab;
        initialParameters.searchContext = this.searchTabConfig.contentSearchContext;

        this.searchParameters$ = new BehaviorSubject<SearchParametersModel>(initialParameters);

        this.searchResult$ = this.searchParameters$.pipe(
            tap(() => this._lockService.lock()),
            tap(parameters => this.setQueryString(parameters)),
            map(parameters => this.mapParameters(parameters)),
            mergeMap(parameters => this._searchService.getSearchResults(parameters)),
            tap(() => this._lockService.unlock()),
            share()
        );
    }

    public ngOnDestroy(): void {
    }

    protected setPageNumber(pageNumber: number): void {
        this.selectedPageNumber = pageNumber;
        const parameters = { ...this.searchParameters$.value };
        parameters.pageNumber = pageNumber ?? 1;
        this.searchParameters$.next(parameters);
    }

    protected setSorting(sorting: string): void {
        this.selectedSorting = sorting;
        const parameters = { ...this.searchParameters$.value };
        parameters.sortOption = sorting;
        parameters.pageNumber = 1;
        this.searchParameters$.next(parameters);
    }

    protected resolveNoResultsMessage(markup: string | null | undefined): string {
        return isRteMarkupEmpty(markup) ? this.defaultNoResultsMessage : (markup as string);
    }

    private mapParameters(parameters: SearchParametersModel): SearchParametersModel {
        const mapped = { ...parameters };

        switch (mapped.sortOption) {
            case "Rank;asc":
                mapped.sortOption = "0";
                break;
            case "Name;asc":
                mapped.sortOption = "1";
                break;
            case "Name;desc":
                mapped.sortOption = "2";
                break;
            default:
                break;
        }

        return mapped;
    }

    private setQueryString(parameters: SearchParametersModel): void {
        this._browserWindowService.setQueryString(this._searchParametersFactory.serialiseToQueryString(parameters));
    }
}
